import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserCheck, UserX, LogOut, Loader2, Edit, Trash2, 
  Clock, UserPlus, Settings as SettingsIcon, Save, Search, ArrowUpDown, 
  Filter, X, Menu, Shield, AlertTriangle, CheckCircle2, ChevronRight,
  ChevronDown, ArrowUpRight, Activity, Calendar, Stethoscope, RefreshCw,
  Sparkles, ExternalLink, MessageCircle, Key, Eye, EyeOff, Copy, Check,
  Mail, Send, Info, PhoneCall, Phone
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminAPI, settingsAPI, patientsAPI, authAPI } from '../services/api';
import ConfirmDialog, { AlertDialog } from '../components/ConfirmDialog';
import BackToTop from '../components/BackToTop';
import AdminSidebar from '../components/AdminSidebar';

export default function AdminDashboard() {
  const [activeMenu, setActiveMenu] = useState('overview'); // 'overview', 'users', 'patients', 'settings'
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Data states
  const [allUsers, setAllUsers] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);

  // User Filter, Search & Sort
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // 'all', 'admin', 'premium', 'trial'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'pending', 'expired', 'rejected'
  const [sortBy, setSortBy] = useState('created_at'); // 'name', 'email', 'created_at', 'role', 'status', 'remaining'
  const [sortOrder, setSortOrder] = useState('desc');

  // Bulk selection
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Edit User Modal
  const [editUserModal, setEditUserModal] = useState({ open: false, user: null, isNew: false });
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    role: 'trial',
    duration_days: 30,
    extend_days: 0,
    new_password: ''
  });
  const [editPasswordVisible, setEditPasswordVisible] = useState(false);
  const [editPasswordCopied, setEditPasswordCopied] = useState(false);

  // Quick Reset Password Modal
  const [resetPasswordModal, setResetPasswordModal] = useState({
    open: false,
    user: null,
    newPassword: '',
    showPassword: true,
    copied: false
  });

  // Settings State
  const [settings, setSettings] = useState({
    whatsapp_number: '',
    whatsapp_message: 'Saya ingin memperpanjang akses NUTRIMUS saya.',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    smtp_from: 'Nutrimus <noreply@nutrimus.web.id>',
    contact_title: 'Contact Us',
    contact_email: 'adminnutrimus@gmail.com',
    contact_phone: '+62 813-6143-9700',
    contact_disclaimer: 'This tools is intended for use only by Clinical Nutrition Specialists or under their supervision. Clinical decisions must be made by medical professionals.',
    contact_copyright: '© 2026 Nutrimus. All rights reserved.'
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [savingContactSettings, setSavingContactSettings] = useState(false);

  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);


  // Patient Audit State
  const [expandedUsers, setExpandedUsers] = useState({});
  const [patientSearchQuery, setPatientSearchQuery] = useState('');

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: 'warning',
    title: '',
    message: '',
    onConfirm: null
  });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    type: 'info',
    title: '',
    message: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    const user = authAPI.getCurrentUser();
    setCurrentUser(user);
    if (!user || user.role !== 'admin') {
      navigate('/patients');
      return;
    }

    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadUsers(), loadSettings(), loadPatients()]);
    } catch (err) {
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminAPI.getAllUsers();
      setAllUsers(response.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.detail || 'Gagal memuat data pengguna');
      if (err.response?.status === 403) {
        navigate('/patients');
      }
    }
  };

  const loadSettings = async () => {
    try {
      const data = await settingsAPI.getSettings();
      setSettings(data);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const response = await patientsAPI.getAll();
      const patientsData = Array.isArray(response) ? response : (response?.patients || []);
      setAllPatients(patientsData);
      
      // Auto-expand first 3 users for convenience
      const initialExpanded = {};
      const uniqueUserIds = [...new Set(patientsData.map(p => p.user_id).filter(Boolean))];
      uniqueUserIds.slice(0, 3).forEach(id => {
        initialExpanded[id] = true;
      });
      setExpandedUsers(initialExpanded);
    } catch (err) {
      console.error('Error loading patient audit data:', err);
    } finally {
      setPatientsLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/');
  };

  // Helper date calculations
  const calculateRemainingDays = (accessExpiredDate) => {
    if (!accessExpiredDate) return 'Selamanya';
    const expiry = new Date(accessExpiredDate);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Kedaluwarsa';
    if (diffDays === 0) return 'Hari ini';
    return `${diffDays} hari`;
  };

  const getRemainingDaysNumber = (accessExpiredDate) => {
    if (!accessExpiredDate) return Infinity;
    const expiry = new Date(accessExpiredDate);
    const now = new Date();
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays < 0 ? -999 : diffDays;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('id-ID', { maximumFractionDigits: 0 });
  };

  // KPI Computations
  const pendingUsers = allUsers.filter(u => u.status === 'pending');
  const activeUsers = allUsers.filter(u => u.status === 'active');
  const expiredUsers = allUsers.filter(u => {
    if (u.role === 'admin') return false;
    if (u.status === 'expired') return true;
    return getRemainingDaysNumber(u.access_expired_date) < 0;
  });
  const expiringSoonUsers = allUsers.filter(u => {
    if (u.role === 'admin') return false;
    const days = getRemainingDaysNumber(u.access_expired_date);
    return days >= 0 && days <= 7;
  });
  const premiumCount = allUsers.filter(u => u.role === 'premium').length;
  const trialCount = allUsers.filter(u => u.role === 'trial').length;

  // Filter & Search for User Table
  const filteredUsers = allUsers
    .filter(u => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    })
    .filter(u => (filterRole === 'all' ? true : u.role === filterRole))
    .filter(u => (filterStatus === 'all' ? true : u.status === filterStatus))
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'role':
          aVal = a.role || '';
          bVal = b.role || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'remaining':
          aVal = getRemainingDaysNumber(a.access_expired_date);
          bVal = getRemainingDaysNumber(b.access_expired_date);
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at || 0).getTime();
          bVal = new Date(b.created_at || 0).getTime();
          break;
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

  // Group patients by User
  const groupedPatients = allPatients
    .filter(p => {
      if (!patientSearchQuery.trim()) return true;
      const q = patientSearchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(q) ||
        p.user_name?.toLowerCase().includes(q) ||
        p.user_email?.toLowerCase().includes(q)
      );
    })
    .reduce((acc, patient) => {
      const uId = patient.user_id || 'unknown';
      if (!acc[uId]) acc[uId] = [];
      acc[uId].push(patient);
      return acc;
    }, {});

  const getUserInfo = (userId) => {
    const foundUser = allUsers.find(u => u.id === userId);
    if (foundUser) return foundUser;
    const patientWithUser = allPatients.find(p => p.user_id === userId);
    if (patientWithUser) {
      return {
        name: patientWithUser.user_name || 'Ahli Gizi',
        email: patientWithUser.user_email || '-'
      };
    }
    return { name: 'User', email: '-' };
  };

  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  // User Actions
  const handleApprove = async (userId, email) => {
    setConfirmDialog({
      open: true,
      type: 'success',
      title: 'Setujui Pengguna',
      message: `Setujui akun ${email}? Akses akun akan langsung diaktifkan sesuai role.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setProcessing(userId);
        try {
          await adminAPI.approveUser(userId);
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `Akun ${email} berhasil disetujui!`
          });
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: err.response?.data?.detail || 'Gagal menyetujui pengguna'
          });
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const handleReject = async (userId, email) => {
    setConfirmDialog({
      open: true,
      type: 'danger',
      title: 'Tolak Pengguna',
      message: `Tolak pendaftaran akun ${email}? Pengguna tidak akan dapat mengakses aplikasi.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setProcessing(userId);
        try {
          await adminAPI.rejectUser(userId);
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `Akun ${email} telah ditolak.`
          });
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: err.response?.data?.detail || 'Gagal menolak pengguna'
          });
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const handleDeleteUser = async (userId, email) => {
    setConfirmDialog({
      open: true,
      type: 'danger',
      title: 'Hapus Pengguna',
      message: `Apakah Anda yakin ingin menghapus akun ${email}? Tindakan ini bersifat permanen.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setProcessing(userId);
        try {
          await adminAPI.deleteUser(userId);
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `Pengguna ${email} telah dihapus.`
          });
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: err.response?.data?.detail || 'Gagal menghapus pengguna'
          });
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const handleQuickExtend = async (userId, email, days = 30) => {
    setConfirmDialog({
      open: true,
      type: 'success',
      title: `Perpanjang Akses ${days} Hari`,
      message: `Perpanjang masa aktif akun ${email} sebanyak ${days} hari?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setProcessing(userId);
        try {
          await adminAPI.extendUserAccess(userId, days);
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `Masa aktif ${email} berhasil diperpanjang ${days} hari.`
          });
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: err.response?.data?.detail || 'Gagal memperpanjang masa aktif'
          });
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 4; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `Nutri${code}!`;
  };

  const openResetPasswordModal = (user) => {
    const initialPass = generateRandomPassword();
    setResetPasswordModal({
      open: true,
      user,
      newPassword: initialPass,
      showPassword: true,
      copied: false
    });
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModal({
      open: false,
      user: null,
      newPassword: '',
      showPassword: true,
      copied: false
    });
  };

  const handleExecuteResetPassword = async () => {
    const { user, newPassword } = resetPasswordModal;
    if (!user || !newPassword) return;

    if (newPassword.trim().length < 6) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Password Terlalu Pendek',
        message: 'Password baru minimal 6 karakter.'
      });
      return;
    }

    setProcessing(user.id);
    try {
      await adminAPI.resetUserPassword(user.id, newPassword.trim());
      closeResetPasswordModal();

      setAlertDialog({
        open: true,
        type: 'success',
        title: 'Password Berhasil Direset!',
        message: `Password akun ${user.email} telah berhasil diubah menjadi: "${newPassword.trim()}". Sesi lama pengguna telah diakhiri demi keamanan.`
      });
      await loadUsers();
    } catch (err) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Gagal Reset Password',
        message: err.response?.data?.detail || 'Gagal mereset password pengguna'
      });
    } finally {
      setProcessing(null);
    }
  };

  const openEditModal = (user) => {
    setEditUserModal({ open: true, user, isNew: false });
    setUserFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'trial',
      duration_days: 30,
      extend_days: 0,
      new_password: ''
    });
    setEditPasswordVisible(false);
    setEditPasswordCopied(false);
  };

  const closeEditModal = () => {
    setEditUserModal({ open: false, user: null, isNew: false });
    setUserFormData({
      name: '',
      email: '',
      role: 'trial',
      duration_days: 30,
      extend_days: 0,
      new_password: ''
    });
  };

  const handleSaveUser = async () => {
    const user = editUserModal.user;
    if (!user) return;

    if (userFormData.new_password?.trim() && userFormData.new_password.trim().length < 6) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Password Terlalu Pendek',
        message: 'Password baru minimal 6 karakter.'
      });
      return;
    }

    setProcessing(user.id);
    try {
      const updateData = {};
      if (userFormData.role !== user.role) updateData.role = userFormData.role;
      if (userFormData.name !== user.name) updateData.name = userFormData.name;
      if (userFormData.role === 'premium' && userFormData.duration_days > 0) {
        updateData.duration_days = userFormData.duration_days;
      }
      if (userFormData.new_password?.trim()) {
        updateData.password = userFormData.new_password.trim();
      }

      if (userFormData.extend_days > 0) {
        await adminAPI.extendUserAccess(user.id, userFormData.extend_days);
      }

      if (Object.keys(updateData).length > 0) {
        await adminAPI.updateUser(user.id, updateData);
      }

      setAlertDialog({
        open: true,
        type: 'success',
        title: 'Berhasil',
        message: `Data pengguna ${user.email} berhasil diperbarui!${userFormData.new_password ? ' Password akun juga berhasil diubah.' : ''}`
      });
      closeEditModal();
      await loadUsers();
    } catch (err) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Gagal',
        message: err.response?.data?.detail || 'Gagal memperbarui pengguna'
      });
    } finally {
      setProcessing(null);
    }
  };

  // Bulk Actions
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
      setSelectAll(false);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
      setSelectAll(true);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleBulkApprove = () => {
    if (selectedUsers.length === 0) return;
    setConfirmDialog({
      open: true,
      type: 'success',
      title: 'Setujui Pengguna Terpilih',
      message: `Setujui ${selectedUsers.length} akun pengguna yang dipilih?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          await Promise.all(selectedUsers.map(id => adminAPI.approveUser(id)));
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `${selectedUsers.length} akun berhasil disetujui!`
          });
          setSelectedUsers([]);
          setSelectAll(false);
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: 'Beberapa pengguna gagal disetujui'
          });
        }
      }
    });
  };

  const handleBulkExtend = (days = 30) => {
    if (selectedUsers.length === 0) return;
    setConfirmDialog({
      open: true,
      type: 'success',
      title: `Perpanjang ${days} Hari Sekaligus`,
      message: `Perpanjang masa aktif ${selectedUsers.length} akun yang dipilih sebanyak ${days} hari?`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          await Promise.all(selectedUsers.map(id => adminAPI.extendUserAccess(id, days)));
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `Masa aktif ${selectedUsers.length} akun berhasil diperpanjang!`
          });
          setSelectedUsers([]);
          setSelectAll(false);
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: 'Gagal memperpanjang beberapa akun'
          });
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedUsers.length === 0) return;
    setConfirmDialog({
      open: true,
      type: 'danger',
      title: 'Hapus Pengguna Terpilih',
      message: `Apakah Anda yakin ingin menghapus ${selectedUsers.length} akun? Tindakan ini tidak dapat dibatalkan.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        try {
          await Promise.all(selectedUsers.map(id => adminAPI.deleteUser(id)));
          setAlertDialog({
            open: true,
            type: 'success',
            title: 'Berhasil',
            message: `${selectedUsers.length} akun berhasil dihapus.`
          });
          setSelectedUsers([]);
          setSelectAll(false);
          await loadUsers();
        } catch (err) {
          setAlertDialog({
            open: true,
            type: 'danger',
            title: 'Gagal',
            message: 'Gagal menghapus beberapa akun'
          });
        }
      }
    });
  };

  // Settings Save
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await settingsAPI.updateSettings({
        whatsapp_number: settings.whatsapp_number,
        whatsapp_message: settings.whatsapp_message
      });
      setAlertDialog({
        open: true,
        type: 'success',
        title: 'Berhasil',
        message: 'Pengaturan kontak WhatsApp berhasil disimpan!'
      });
    } catch (err) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Gagal',
        message: err.response?.data?.detail || 'Gagal menyimpan pengaturan'
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveContactSettings = async () => {
    setSavingContactSettings(true);
    try {
      await settingsAPI.updateSettings({
        contact_title: settings.contact_title || 'Contact Us',
        contact_email: settings.contact_email || '',
        contact_phone: settings.contact_phone || '',
        contact_disclaimer: settings.contact_disclaimer || '',
        contact_copyright: settings.contact_copyright || ''
      });
      setAlertDialog({
        open: true,
        type: 'success',
        title: 'Berhasil Disimpan',
        message: 'Pengaturan Contact Us / Footer berhasil diperbarui dan langsung tampil di seluruh aplikasi!'
      });
    } catch (err) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Gagal Menyimpan',
        message: err.response?.data?.detail || 'Gagal menyimpan pengaturan Contact Us'
      });
    } finally {
      setSavingContactSettings(false);
    }
  };

  const handleSaveEmailSettings = async () => {
    setSavingEmailSettings(true);
    try {
      await settingsAPI.updateSettings({
        smtp_host: settings.smtp_host,
        smtp_port: parseInt(settings.smtp_port) || 587,
        smtp_user: settings.smtp_user,
        smtp_pass: settings.smtp_pass,
        smtp_from: settings.smtp_from
      });
      setAlertDialog({
        open: true,
        type: 'success',
        title: 'Berhasil',
        message: 'Pengaturan email SMTP berhasil disimpan!'
      });
    } catch (err) {
      setAlertDialog({
        open: true,
        type: 'danger',
        title: 'Gagal',
        message: err.response?.data?.detail || 'Gagal menyimpan pengaturan email'
      });
    } finally {
      setSavingEmailSettings(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailTo.trim()) {
      setAlertDialog({
        open: true,
        type: 'warning',
        title: 'Email Penerima Diperlukan',
        message: 'Silakan masukkan alamat email penerima untuk pengujian.'
      });
      return;
    }

    setSendingTestEmail(true);
    setTestEmailResult(null);
    try {
      const res = await settingsAPI.testEmail({
        to_email: testEmailTo.trim(),
        smtp_host: settings.smtp_host,
        smtp_port: parseInt(settings.smtp_port) || 587,
        smtp_user: settings.smtp_user,
        smtp_pass: settings.smtp_pass,
        smtp_from: settings.smtp_from
      });
      setTestEmailResult({
        success: true,
        message: res.message || `Email uji coba berhasil dikirim ke ${testEmailTo}!`
      });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Gagal mengirim email uji coba.';
      setTestEmailResult({
        success: false,
        message: errorMsg
      });
    } finally {
      setSendingTestEmail(false);
    }
  };


  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">Admin</span>;
      case 'premium':
        return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">Premium</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 border border-sky-200">Trial</span>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">Aktif</span>;
      case 'pending':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 animate-pulse">Pending</span>;
      case 'expired':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">Expired</span>;
      case 'rejected':
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">Ditolak</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{status}</span>;
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full animate-in fade-in">
      {/* Sub Navigation Tabs Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 rounded-2xl border border-slate-200 shadow-xs">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveMenu('overview')}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
              activeMenu === 'overview'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Activity className="h-4 w-4" />
            <span>Ringkasan Sistem</span>
          </button>
          <button
            onClick={() => setActiveMenu('users')}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 relative ${
              activeMenu === 'users'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Kelola Pengguna ({allUsers.length})</span>
          </button>
          <button
            onClick={() => setActiveMenu('contact')}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
              activeMenu === 'contact'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <PhoneCall className="h-4 w-4" />
            <span>Pengaturan Contact Us</span>
          </button>
          <button
            onClick={() => setActiveMenu('settings')}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
              activeMenu === 'settings'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <MessageCircle className="h-4 w-4" />
            <span>Pengaturan WhatsApp</span>
          </button>
          <button
            onClick={() => setActiveMenu('email')}
            className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
              activeMenu === 'email'
                ? 'bg-slate-900 text-white shadow-xs'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Mail className="h-4 w-4" />
            <span>Pengaturan Email</span>
          </button>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={loadInitialData}
            disabled={loading}
            className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900"
            title="Muat ulang data"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin text-emerald-600' : ''}`} />
            <span className="hidden sm:inline ml-1.5">Refresh</span>
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="p-1 text-red-500 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

          {/* ======================= TAB 1: OVERVIEW ======================= */}
          {activeMenu === 'overview' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              {/* Top 4 KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white border-slate-200/80 shadow-xs hover:shadow-sm transition-shadow">
                  <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Total Pengguna</p>
                      <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{allUsers.length}</h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {premiumCount} Premium • {trialCount} Trial
                      </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
                      <Users className="h-5 w-5" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200/80 shadow-xs hover:shadow-sm hover:border-emerald-200 transition-all">
                  <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Pengguna Aktif</p>
                      <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{activeUsers.length}</h3>
                      <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                        Akses aktif berjalan normal
                      </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <UserCheck className="h-5 w-5" />
                    </div>
                  </CardContent>
                </Card>

                <Card className={`border shadow-xs transition-shadow ${
                  expiringSoonUsers.length > 0 
                    ? 'bg-rose-50/40 border-rose-200 hover:border-rose-300' 
                    : 'bg-white border-slate-200/80'
                }`}>
                  <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                    <div>
                      <p className={`text-xs font-medium ${expiringSoonUsers.length > 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                        Segera Expired (&le;7 Hari)
                      </p>
                      <h3 className={`text-2xl sm:text-3xl font-bold mt-1 ${expiringSoonUsers.length > 0 ? 'text-rose-900' : 'text-slate-900'}`}>
                        {expiringSoonUsers.length}
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {expiredUsers.length} akun telah kedaluwarsa
                      </p>
                    </div>
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      expiringSoonUsers.length > 0 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-slate-200/80 shadow-xs hover:shadow-sm transition-shadow">
                  <CardContent className="p-4 sm:p-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Pasien Terdaftar</p>
                      <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">{allPatients.length}</h3>
                      <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                        {Object.keys(groupedPatients).length} Ahli Gizi Aktif
                      </p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <Activity className="h-5 w-5" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Grid 2 Columns: Expiring Soon Follow-up & Recent Registered Users */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Expiring Soon Card */}
                <Card className="border-slate-200 shadow-xs">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-rose-500" />
                        <CardTitle className="text-sm font-semibold">Peringatan Akses Segera Habis</CardTitle>
                      </div>
                      <span className="text-xs text-slate-400">&le; 7 hari</span>
                    </div>
                    <CardDescription className="text-xs">
                      Pengguna yang memerlukan perpanjangan masa aktif segera
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 divide-y divide-slate-100">
                    {expiringSoonUsers.length === 0 ? (
                      <div className="p-6 text-center text-xs text-slate-400">
                        <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2 opacity-80" />
                        Tidak ada akun yang akan kedaluwarsa dalam 7 hari ke depan.
                      </div>
                    ) : (
                      expiringSoonUsers.slice(0, 5).map(u => (
                        <div key={u.id} className="p-3.5 sm:p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">{u.name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                            <span className="text-[10px] font-semibold text-rose-600">
                              Sisa: {calculateRemainingDays(u.access_expired_date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleQuickExtend(u.id, u.email, 30)}
                              disabled={processing === u.id}
                              className="h-7 px-2 text-[11px] font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            >
                              +30 Hari
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>

                {/* Recent Registrations Card */}
                <Card className="border-slate-200 shadow-xs">
                  <CardHeader className="pb-3 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-emerald-600" />
                        <CardTitle className="text-sm font-semibold">Pendaftaran Terbaru</CardTitle>
                      </div>
                      <button
                        onClick={() => setActiveMenu('users')}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-800"
                      >
                        Lihat Semua &rarr;
                      </button>
                    </div>
                    <CardDescription className="text-xs">
                      Akun yang baru saja mendaftar ke sistem
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 divide-y divide-slate-100">
                    {allUsers.slice(0, 5).map(u => (
                      <div key={u.id} className="p-3.5 sm:p-4 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                            {u.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800 truncate">{u.name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getRoleBadge(u.role)}
                          {getStatusBadge(u.status)}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* ======================= TAB 2: USER MANAGEMENT ======================= */}
          {activeMenu === 'users' && (
            <div className="space-y-6 animate-in fade-in duration-150">
              {/* Search, Filter & Bulk Controls Bar */}
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
                <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                  {/* Search Bar */}
                  <div className="relative w-full md:max-w-md">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Cari pengguna berdasarkan nama atau email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-10 text-sm bg-slate-50/60 border-slate-200 focus:bg-white"
                    />
                  </div>

                  {/* Filter Role & Status Dropdowns */}
                  <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="all">Semua Role</option>
                      <option value="premium">Premium</option>
                      <option value="trial">Trial</option>
                      <option value="admin">Admin</option>
                    </select>

                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="all">Semua Status</option>
                      <option value="active">Aktif</option>
                      <option value="expired">Expired</option>
                      <option value="rejected">Ditolak</option>
                    </select>

                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="created_at">Urutkan: Tanggal Daftar</option>
                      <option value="name">Urutkan: Nama</option>
                      <option value="email">Urutkan: Email</option>
                      <option value="remaining">Urutkan: Sisa Hari</option>
                    </select>
                  </div>
                </div>

                {/* Bulk Action Toolbar if items selected */}
                {selectedUsers.length > 0 && (
                  <div className="p-3 bg-emerald-50/80 border border-emerald-200 rounded-lg flex flex-wrap items-center justify-between gap-3 animate-in fade-in">
                    <div className="text-xs font-semibold text-emerald-900">
                      {selectedUsers.length} pengguna dipilih
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkExtend(30)}
                        className="h-7 text-xs bg-white border-emerald-300 text-emerald-800 hover:bg-emerald-100 font-medium"
                      >
                        +30 Hari
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleBulkDelete}
                        className="h-7 text-xs border-red-200 text-red-700 hover:bg-red-50"
                      >
                        Hapus ({selectedUsers.length})
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Users Table / Mobile Cards */}
              <div className="bg-white rounded-xl shadow-xs border border-slate-200 overflow-hidden">
                {/* Mobile View (md:hidden): Touch-friendly Cards */}
                <div className="md:hidden divide-y divide-slate-100">
                  {filteredUsers.length === 0 ? (
                    <div className="p-8 text-center text-xs text-slate-400">
                      Tidak ada pengguna yang cocok dengan filter pencarian.
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <div key={user.id} className="p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => handleSelectUser(user.id)}
                              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <div className="min-w-0">
                              <h4 className="text-sm font-semibold text-slate-900 truncate">{user.name}</h4>
                              <p className="text-xs text-slate-500 truncate">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {getRoleBadge(user.role)}
                            {getStatusBadge(user.status)}
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                          <span>Daftar: {formatDate(user.created_at)}</span>
                          <span className={`font-semibold ${
                            getRemainingDaysNumber(user.access_expired_date) < 0 ? 'text-red-600' :
                            getRemainingDaysNumber(user.access_expired_date) <= 7 ? 'text-amber-600' : 'text-slate-700'
                          }`}>
                            Sisa: {calculateRemainingDays(user.access_expired_date)}
                          </span>
                        </div>

                        <div className="flex items-center justify-end gap-1.5 pt-2 border-t border-slate-100">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openResetPasswordModal(user)}
                            className="h-7 px-2 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
                            title="Reset Password Pengguna"
                          >
                            <Key className="h-3 w-3 mr-1" /> Password
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(user)}
                            className="h-7 px-2 text-xs text-slate-600"
                          >
                            <Edit className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuickExtend(user.id, user.email, 30)}
                            className="h-7 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          >
                            +30 Hari
                          </Button>
                          <button
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                            title="Hapus"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Desktop View (hidden md:block): Full Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50/80 text-xs text-slate-600 uppercase font-semibold">
                      <tr>
                        <th className="w-10 px-4 py-3.5 text-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                          />
                        </th>
                        <th className="px-5 py-3.5 text-left">Pengguna</th>
                        <th className="px-4 py-3.5 text-left">Role</th>
                        <th className="px-4 py-3.5 text-left">Status</th>
                        <th className="px-4 py-3.5 text-left">Sisa Masa Aktif</th>
                        <th className="px-4 py-3.5 text-left">Tanggal Daftar</th>
                        <th className="px-5 py-3.5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                            Tidak ada pengguna yang cocok dengan kriteria pencarian.
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map(user => {
                          const remainingDays = getRemainingDaysNumber(user.access_expired_date);
                          const isExpiringSoon = remainingDays >= 0 && remainingDays <= 7;
                          const isExpired = remainingDays < 0;

                          return (
                            <tr key={user.id} className="hover:bg-slate-50/70 transition-colors">
                              <td className="px-4 py-3.5 text-center">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={() => handleSelectUser(user.id)}
                                  className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                              </td>
                              <td className="px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                    {user.name?.charAt(0).toUpperCase() || 'U'}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-semibold text-slate-900 truncate">{user.name}</p>
                                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3.5">
                                {getRoleBadge(user.role)}
                              </td>
                              <td className="px-4 py-3.5">
                                {getStatusBadge(user.status)}
                              </td>
                              <td className="px-4 py-3.5 text-xs">
                                <span className={`font-semibold ${
                                  isExpired ? 'text-red-600' :
                                  isExpiringSoon ? 'text-amber-600' : 'text-slate-800'
                                }`}>
                                  {calculateRemainingDays(user.access_expired_date)}
                                </span>
                                {user.access_expired_date && (
                                  <div className="text-[11px] text-slate-400">
                                    Sampai {formatDate(user.access_expired_date)}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3.5 text-xs text-slate-500">
                                {formatDate(user.created_at)}
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openResetPasswordModal(user)}
                                    className="h-8 px-2 text-xs text-amber-700 hover:bg-amber-50 font-medium"
                                    title="Reset Password Pengguna"
                                  >
                                    <Key className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => openEditModal(user)}
                                    className="h-8 px-2 text-xs text-slate-600 hover:text-slate-900"
                                    title="Edit Akses"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleQuickExtend(user.id, user.email, 30)}
                                    className="h-8 px-2 text-xs text-emerald-700 hover:bg-emerald-50 font-medium"
                                    title="Perpanjang 30 Hari"
                                  >
                                    +30 Hari
                                  </Button>
                                  <button
                                    onClick={() => handleDeleteUser(user.id, user.email)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Hapus Pengguna"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ======================= TAB: CONTACT US SETTINGS ======================= */}
          {activeMenu === 'contact' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in duration-150">
              {/* Card 1: Live Preview matching the user's screenshot */}
              <Card className="border-slate-200 shadow-xs overflow-hidden">
                <CardHeader className="bg-slate-50/80 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 rounded-xl bg-green-100 text-green-700 border border-green-200">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900">Pratinjau Langsung (Live Preview)</CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Tampilan ini yang akan langsung dilihat oleh pengguna di bagian footer seluruh halaman aplikasi.
                        </CardDescription>
                      </div>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      Realtime Sync
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-5 sm:p-6 bg-slate-900/5">
                  {/* Clean Minimalist Option A Preview Box */}
                  <div className="bg-white border border-slate-200/90 text-slate-600 rounded-2xl p-5 sm:p-6 shadow-sm transition-all">
                    {/* Contact Us header and items */}
                    <div className="mb-4">
                      <h4 className="font-bold text-xs uppercase tracking-wider text-slate-400 mb-2.5">
                        {settings.contact_title || 'Contact Us'}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-2.5 text-xs sm:text-sm">
                        <div className="flex items-center">
                          <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 mr-2 flex-shrink-0">
                            <Mail className="h-3.5 w-3.5" />
                          </div>
                          <span className="font-medium text-slate-700">
                            {settings.contact_email || 'adminnutrimus@gmail.com'}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 mr-2 flex-shrink-0">
                            <Phone className="h-3.5 w-3.5" />
                          </div>
                          <span className="font-medium text-slate-700">
                            {settings.contact_phone || '+62 813-6143-9700'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Medical Disclaimer */}
                    {settings.contact_disclaimer && (
                      <div className="bg-emerald-50/70 border border-emerald-100/80 rounded-xl p-3.5 mb-4">
                        <p className="text-xs text-emerald-950 leading-relaxed">
                          <span className="font-semibold text-emerald-800">Medical Disclaimer:</span> {settings.contact_disclaimer.replace(/^Medical Disclaimer:\s*/i, '')}
                        </p>
                      </div>
                    )}

                    {/* Copyright */}
                    <div className="pt-3 border-t border-slate-100 text-center">
                      <p className="text-xs text-slate-400">
                        {settings.contact_copyright || '© 2026 Nutrimus. All rights reserved.'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Edit Form */}
              <Card className="border-slate-200 shadow-xs">
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                      <PhoneCall className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900">Ubah Tampilan Contact Us & Footer</CardTitle>
                      <CardDescription className="text-xs text-slate-500">
                        Sesuaikan judul, email, telepon, teks disclaimer medis, dan hak cipta footer.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact_title" className="text-xs font-semibold text-slate-700">
                      Judul Bagian Kontak (Contact Title)
                    </Label>
                    <Input
                      id="contact_title"
                      type="text"
                      placeholder="Contoh: Contact Us atau Hubungi Kami"
                      value={settings.contact_title || ''}
                      onChange={(e) => setSettings({ ...settings, contact_title: e.target.value })}
                      className="text-sm bg-slate-50/60"
                    />
                    <p className="text-[11px] text-slate-400">
                      Judul yang tampil di atas baris email dan nomor telepon.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="contact_email" className="text-xs font-semibold text-slate-700">
                        Alamat Email Kontak
                      </Label>
                      <div className="relative">
                        <Input
                          id="contact_email"
                          type="email"
                          placeholder="adminnutrimus@gmail.com"
                          value={settings.contact_email || ''}
                          onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                          className="text-sm bg-slate-50/60 pl-9"
                        />
                        <Mail className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Email bantuan layanan yang bisa diklik langsung (mailto:).
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="contact_phone" className="text-xs font-semibold text-slate-700">
                        Nomor Telepon / WhatsApp
                      </Label>
                      <div className="relative">
                        <Input
                          id="contact_phone"
                          type="text"
                          placeholder="+62 813-6143-9700"
                          value={settings.contact_phone || ''}
                          onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                          className="text-sm bg-slate-50/60 pl-9"
                        />
                        <Phone className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Nomor panggilan telepon atau WhatsApp bantuan.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="contact_disclaimer" className="text-xs font-semibold text-slate-700">
                      Teks Medical Disclaimer
                    </Label>
                    <textarea
                      id="contact_disclaimer"
                      rows={3}
                      placeholder="Teks pernyataan batasan tanggung jawab medis..."
                      value={settings.contact_disclaimer || ''}
                      onChange={(e) => setSettings({ ...settings, contact_disclaimer: e.target.value })}
                      className="w-full p-3 rounded-lg border border-slate-200 text-sm bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                    <p className="text-[11px] text-slate-400">
                      Pernyataan penggunaan medis yang ditampilkan di kotak hijau gelap.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="contact_copyright" className="text-xs font-semibold text-slate-700">
                      Teks Hak Cipta (Copyright)
                    </Label>
                    <Input
                      id="contact_copyright"
                      type="text"
                      placeholder="© 2026 Nutrimus. All rights reserved."
                      value={settings.contact_copyright || ''}
                      onChange={(e) => setSettings({ ...settings, contact_copyright: e.target.value })}
                      className="text-sm bg-slate-50/60"
                    />
                    <p className="text-[11px] text-slate-400">
                      Teks baris paling bawah pada footer aplikasi.
                    </p>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <Button
                      onClick={handleSaveContactSettings}
                      disabled={savingContactSettings}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                    >
                      {savingContactSettings ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                      )}
                      Simpan Pengaturan Contact Us
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ======================= TAB 3: SETTINGS ======================= */}
          {activeMenu === 'settings' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in duration-150">
              <Card className="border-slate-200 shadow-xs">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <MessageCircle className="h-5 w-5 text-emerald-600" />
                    <CardTitle className="text-base font-semibold">Pengaturan Kontak WhatsApp Admin</CardTitle>
                  </div>
                  <CardDescription className="text-xs">
                    Nomor WhatsApp ini digunakan sebagai tombol perpanjangan masa aktif saat akun pengguna kedaluwarsa.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="wa_num" className="text-xs font-semibold text-slate-700">
                      Nomor WhatsApp Admin (Format Internasional)
                    </Label>
                    <Input
                      id="wa_num"
                      type="text"
                      placeholder="Contoh: 6281234567890"
                      value={settings.whatsapp_number}
                      onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                      className="text-sm bg-slate-50/60"
                    />
                    <p className="text-[11px] text-slate-400">
                      Gunakan format kode negara tanpa spasi atau simbol plus (misal: 6281xxxx).
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="wa_msg" className="text-xs font-semibold text-slate-700">
                      Format Pesan Default dari Pengguna
                    </Label>
                    <textarea
                      id="wa_msg"
                      rows={4}
                      value={settings.whatsapp_message}
                      onChange={(e) => setSettings({ ...settings, whatsapp_message: e.target.value })}
                      className="w-full p-3 rounded-lg border border-slate-200 text-sm bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>

                  {/* WhatsApp Preview Card */}
                  <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-100 flex items-start gap-3 text-xs text-slate-600">
                    <Sparkles className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-semibold text-emerald-900">Pratinjau Pesan yang Diterima:</span>
                      <p className="mt-1 italic text-slate-700 bg-white p-2.5 rounded border border-emerald-200/60">
                        "{settings.whatsapp_message || 'Halo Admin...'}"
                      </p>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <Button
                      onClick={handleSaveSettings}
                      disabled={savingSettings}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                    >
                      {savingSettings ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                      )}
                      Simpan Pengaturan
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* System Info Card */}
              <Card className="border-slate-200 shadow-xs">
                <CardHeader>
                  <CardTitle className="text-sm font-semibold">Informasi Status Sistem</CardTitle>
                </CardHeader>
                <CardContent className="p-5 pt-0 space-y-2.5 text-xs text-slate-600">
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span>Aplikasi</span>
                    <span className="font-semibold text-slate-900">NUTRIMUS Clinical Nutrition Recall</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span>Versi Portal</span>
                    <span className="font-semibold text-slate-900">v2.1 Enterprise</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span>Backend Status</span>
                    <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span> Online (FastAPI)
                    </span>
                  </div>
                  <div className="flex justify-between py-1.5">
                    <span>Database</span>
                    <span className="font-semibold text-slate-900">MongoDB Database Engine</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ======================= TAB 4: EMAIL SETTINGS ======================= */}
          {activeMenu === 'email' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in duration-150">
              <Card className="border-slate-200 shadow-xs">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900">Pengaturan Server Email (SMTP)</CardTitle>
                        <CardDescription className="text-xs text-slate-500">
                          Konfigurasi pengiriman email keluar untuk link Lupa Kata Sandi dan notifikasi sistem.
                        </CardDescription>
                      </div>
                    </div>
                    <div>
                      {settings.smtp_host && settings.smtp_user ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Terkonfigurasi
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Belum Lengkap
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label htmlFor="smtp_host" className="text-xs font-semibold text-slate-700">
                        SMTP Host / Server
                      </Label>
                      <Input
                        id="smtp_host"
                        type="text"
                        placeholder="Contoh: smtp.gmail.com"
                        value={settings.smtp_host || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                        className="text-sm bg-slate-50/60"
                      />
                      <p className="text-[11px] text-slate-400">
                        Domain atau host mail server keluar (misal: smtp.gmail.com).
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_port" className="text-xs font-semibold text-slate-700">
                        SMTP Port
                      </Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        placeholder="587"
                        value={settings.smtp_port || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                        className="text-sm bg-slate-50/60"
                      />
                      <p className="text-[11px] text-slate-400">
                        587 (TLS/STARTTLS) atau 465 (SSL).
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_user" className="text-xs font-semibold text-slate-700">
                        Username / Email SMTP
                      </Label>
                      <Input
                        id="smtp_user"
                        type="email"
                        placeholder="admin@gmail.com atau user@domain.com"
                        value={settings.smtp_user || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                        className="text-sm bg-slate-50/60"
                      />
                      <p className="text-[11px] text-slate-400">
                        Alamat email untuk autentikasi login server SMTP.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="smtp_pass" className="text-xs font-semibold text-slate-700">
                        Password / Sandi Aplikasi SMTP
                      </Label>
                      <div className="relative">
                        <Input
                          id="smtp_pass"
                          type={showSmtpPass ? 'text' : 'password'}
                          placeholder="••••••••••••••••"
                          value={settings.smtp_pass || ''}
                          onChange={(e) => setSettings({ ...settings, smtp_pass: e.target.value })}
                          className="text-sm bg-slate-50/60 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPass(!showSmtpPass)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showSmtpPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Untuk akun Gmail wajib menggunakan App Password (16 karakter).
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="smtp_from" className="text-xs font-semibold text-slate-700">
                      Nama & Email Pengirim (From Header)
                    </Label>
                    <Input
                      id="smtp_from"
                      type="text"
                      placeholder="Nutrimus <noreply@nutrimus.web.id>"
                      value={settings.smtp_from || ''}
                      onChange={(e) => setSettings({ ...settings, smtp_from: e.target.value })}
                      className="text-sm bg-slate-50/60"
                    />
                    <p className="text-[11px] text-slate-400">
                      Format: <span className="font-mono text-slate-600">Nutrimus &lt;noreply@nutrimus.web.id&gt;</span>
                    </p>
                  </div>

                  {/* Gmail Help Callout */}
                  <div className="p-3.5 bg-sky-50/70 rounded-xl border border-sky-100 flex items-start gap-3 text-xs text-sky-900">
                    <Info className="h-4 w-4 text-sky-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="font-semibold text-sky-950">Petunjuk Pengaturan Gmail / Google Workspace:</p>
                      <ol className="list-decimal pl-4 space-y-0.5 text-[11px] text-sky-800">
                        <li>Pastikan fitur <strong>Verifikasi 2 Langkah (2-Step Verification)</strong> aktif pada akun Google pengirim.</li>
                        <li>Buka menu <span className="font-mono bg-sky-100/80 px-1 py-0.5 rounded">Kelola Akun Google &gt; Keamanan &gt; Sandi Aplikasi</span>.</li>
                        <li>Buat sandi aplikasi baru (misal: "Nutrimus") dan salin 16 karakter sandi ke kolom password di atas.</li>
                        <li>Gunakan Host: <span className="font-mono font-medium">smtp.gmail.com</span> dan Port: <span className="font-mono font-medium">587</span>.</li>
                      </ol>
                    </div>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <Button
                      onClick={handleSaveEmailSettings}
                      disabled={savingEmailSettings}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
                    >
                      {savingEmailSettings ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                      )}
                      Simpan Pengaturan Email
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Test Email Delivery */}
              <Card className="border-slate-200 shadow-xs">
                <CardHeader>
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-purple-50 text-purple-600 border border-purple-100">
                      <Send className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold text-slate-900">Uji Coba Pengiriman Email</CardTitle>
                      <CardDescription className="text-xs text-slate-500">
                        Kirimkan email uji coba untuk memvalidasi bahwa koneksi SMTP berfungsi secara langsung.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="test_email" className="text-xs font-semibold text-slate-700">
                      Alamat Email Penerima Tes
                    </Label>
                    <div className="flex flex-col sm:flex-row gap-2.5">
                      <Input
                        id="test_email"
                        type="email"
                        placeholder="Masukkan email penerima (contoh: nama@gmail.com)"
                        value={testEmailTo}
                        onChange={(e) => {
                          setTestEmailTo(e.target.value);
                          if (testEmailResult) setTestEmailResult(null);
                        }}
                        className="text-sm bg-slate-50/60 flex-1"
                      />
                      <Button
                        onClick={handleSendTestEmail}
                        disabled={sendingTestEmail || !testEmailTo.trim()}
                        className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium sm:w-auto w-full flex-shrink-0"
                      >
                        {sendingTestEmail ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                            Mengirim...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1.5" />
                            Kirim Email Tes
                          </>
                        )}
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      Sistem akan mengirim pesan verifikasi HTML ke alamat ini menggunakan parameter SMTP di atas.
                    </p>
                  </div>

                  {/* Feedback Message */}
                  {testEmailResult && (
                    <div
                      className={`p-3.5 rounded-xl border flex items-start gap-3 text-xs transition-all ${
                        testEmailResult.success
                          ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
                          : 'bg-red-50 text-red-900 border-red-200'
                      }`}
                    >
                      {testEmailResult.success ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="space-y-0.5">
                        <p className="font-bold">
                          {testEmailResult.success ? 'Koneksi Email Berhasil!' : 'Koneksi Email Gagal'}
                        </p>
                        <p className="text-[11px] leading-relaxed break-words">{testEmailResult.message}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

      {/* Edit User Modal Dialog */}
      {editUserModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-bold text-base text-slate-900">Edit Akses Pengguna</h3>
              <button onClick={closeEditModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <Label className="font-semibold text-slate-700">Nama Pengguna</Label>
                <Input
                  type="text"
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  className="mt-1 text-xs"
                />
              </div>

              <div>
                <Label className="font-semibold text-slate-700">Email (Read-only)</Label>
                <Input
                  type="text"
                  value={userFormData.email}
                  disabled
                  className="mt-1 text-xs bg-slate-100 text-slate-500 cursor-not-allowed"
                />
              </div>

              <div>
                <Label className="font-semibold text-slate-700">Peran Akun (Role)</Label>
                <select
                  value={userFormData.role}
                  onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                  className="mt-1 w-full h-9 px-3 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="trial">Trial (Batas 10 Pasien, Masa Aktif 10 Hari)</option>
                  <option value="premium">Premium (Batas 60 Pasien, Masa Aktif Sesuai Perpanjangan)</option>
                  <option value="admin">Administrator (Tanpa Batas)</option>
                </select>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <Label className="font-semibold text-slate-700">
                  Tambah Masa Aktif (Hari Tambahan)
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={userFormData.extend_days}
                    onChange={(e) => setUserFormData({ ...userFormData, extend_days: parseInt(e.target.value) || 0 })}
                    className="text-xs"
                  />
                  <div className="flex gap-1">
                    {[7, 30, 90].map(d => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setUserFormData({ ...userFormData, extend_days: d })}
                        className="px-2.5 py-1 text-xs font-semibold rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                      >
                        +{d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-slate-100 space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold text-slate-700 flex items-center gap-1.5">
                    <Key className="h-3.5 w-3.5 text-amber-600" />
                    Reset Password Akun (Opsional)
                  </Label>
                  <button
                    type="button"
                    onClick={() => {
                      const rand = generateRandomPassword();
                      setUserFormData({ ...userFormData, new_password: rand });
                      setEditPasswordVisible(true);
                      setEditPasswordCopied(false);
                    }}
                    className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 hover:underline flex items-center gap-1"
                  >
                    <Sparkles className="h-3 w-3" /> Buat Acak
                  </button>
                </div>
                <div className="relative mt-1">
                  <Input
                    type={editPasswordVisible ? "text" : "password"}
                    placeholder="Kosongkan jika tidak ingin mengubah password"
                    value={userFormData.new_password}
                    onChange={(e) => setUserFormData({ ...userFormData, new_password: e.target.value })}
                    className="text-xs pr-16 font-mono"
                  />
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    {userFormData.new_password && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(userFormData.new_password);
                          setEditPasswordCopied(true);
                          setTimeout(() => setEditPasswordCopied(false), 2000);
                        }}
                        className="p-1 rounded text-slate-400 hover:text-slate-600"
                        title="Salin Password"
                      >
                        {editPasswordCopied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditPasswordVisible(!editPasswordVisible)}
                      className="p-1 rounded text-slate-400 hover:text-slate-600"
                      title={editPasswordVisible ? "Sembunyikan" : "Tampilkan"}
                    >
                      {editPasswordVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                </div>
                {editPasswordCopied && (
                  <p className="text-[11px] text-emerald-600 font-medium">✓ Password disalin ke clipboard</p>
                )}
                <p className="text-[10px] text-slate-400">
                  Minimal 6 karakter. Bila diisi, sesi login lama pengguna akan otomatis diakhiri.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={closeEditModal} className="text-xs">
                Batal
              </Button>
              <Button
                size="sm"
                onClick={handleSaveUser}
                disabled={processing !== null}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Simpan Perubahan
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Reset Password Modal */}
      {resetPasswordModal.open && resetPasswordModal.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-slate-100 space-y-4">
            <div className="flex items-start justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
                  <Key className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-base">Reset Password Pengguna</h3>
                  <p className="text-xs text-slate-500 truncate max-w-[240px]">{resetPasswordModal.user.name} ({resetPasswordModal.user.email})</p>
                </div>
              </div>
              <button
                onClick={closeResetPasswordModal}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-3 bg-amber-50/80 rounded-xl border border-amber-200/60 text-xs text-amber-900 space-y-1">
              <p className="font-semibold flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5 text-amber-700" />
                Setel Ulang Kata Sandi Langsung
              </p>
              <p className="text-amber-800 text-[11px] leading-relaxed">
                Anda dapat menentukan password baru secara manual atau gunakan tombol acak. Sesi login lama akun ini akan otomatis dibatalkan demi keamanan.
              </p>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <Label className="font-semibold text-slate-700">Password Baru</Label>
                <button
                  type="button"
                  onClick={() => setResetPasswordModal(prev => ({ ...prev, newPassword: generateRandomPassword(), copied: false }))}
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline flex items-center gap-1"
                >
                  <Sparkles className="h-3 w-3" /> Buat Acak
                </button>
              </div>

              <div className="relative">
                <Input
                  type={resetPasswordModal.showPassword ? "text" : "password"}
                  value={resetPasswordModal.newPassword}
                  onChange={(e) => setResetPasswordModal(prev => ({ ...prev, newPassword: e.target.value, copied: false }))}
                  className="pr-20 text-xs font-mono tracking-wide"
                  placeholder="Masukkan password minimal 6 karakter..."
                />
                <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(resetPasswordModal.newPassword);
                      setResetPasswordModal(prev => ({ ...prev, copied: true }));
                      setTimeout(() => setResetPasswordModal(prev => ({ ...prev, copied: false })), 2000);
                    }}
                    className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title="Salin Password"
                  >
                    {resetPasswordModal.copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetPasswordModal(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                    className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    title={resetPasswordModal.showPassword ? "Sembunyikan" : "Tampilkan"}
                  >
                    {resetPasswordModal.showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              {resetPasswordModal.copied && (
                <p className="text-[11px] text-emerald-700 font-medium animate-in fade-in">
                  ✓ Password berhasil disalin ke clipboard!
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={closeResetPasswordModal}
                disabled={processing !== null}
                className="text-xs"
              >
                Batal
              </Button>
              <Button
                size="sm"
                onClick={handleExecuteResetPassword}
                disabled={processing !== null || !resetPasswordModal.newPassword}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
              >
                {processing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Key className="h-3.5 w-3.5 mr-1.5" />}
                Simpan &amp; Terapkan Password
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation and Alert Dialogs */}
      <ConfirmDialog
        open={confirmDialog.open}
        type={confirmDialog.type}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />

      <AlertDialog
        open={alertDialog.open}
        type={alertDialog.type}
        title={alertDialog.title}
        message={alertDialog.message}
        onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
      />

      <BackToTop />
    </div>
  );
}
